import { useQuery } from '@tanstack/react-query'
import { ArrowLeftRight, BookPlus, UserRoundSearch, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { errorMessage } from '@/api/errors'
import { EmptyState, ErrorState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { FormField } from '@/components/forms/FormField'
import { Alert } from '@/components/ui/Alert'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import { IconButton } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Kbd } from '@/components/ui/Kbd'
import { PageHeader } from '@/components/ui/PageHeader'
import { Skeleton } from '@/components/ui/Skeleton'
import { useAuth } from '@/features/auth/useAuth'
import {
  booksQuery,
  borrowerQuery,
  borrowersQuery,
  librarySettingsQuery,
  useIssueLoan,
} from '@/features/library/api'
import { AvailabilityBadge } from '@/features/library/components/Badges'
import { BookCover } from '@/features/library/components/BookCover'
import { LoanTable } from '@/features/library/components/LoanTable'
import { SearchCombobox } from '@/features/library/components/SearchCombobox'
import { addDaysIso, isPositiveAmount, todayIso } from '@/features/library/status'
import type { Book, Borrower, BorrowerKind } from '@/features/library/types'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { formatMoney, humanize } from '@/utils/format'

function parseBorrower(value: string | null): { kind: BorrowerKind; id: number } | null {
  const m = /^(student|staff)-(\d+)$/.exec(value ?? '')
  return m ? { kind: m[1] as BorrowerKind, id: Number(m[2]) } : null
}

function BorrowerLine({ b }: { b: Borrower }) {
  const { t } = useTranslation('library')
  return (
    <div className="flex items-center gap-3">
      <Avatar name={b.name} src={b.photoUrl} size="sm" />
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-ink">{b.name}</p>
        <p className="truncate text-xs text-muted">
          {humanize(b.kind)} · <span className="font-mono">{b.code}</span>
          {b.subtitle ? ` · ${b.subtitle}` : ''}
        </p>
      </div>
      <span className="shrink-0 text-xs text-muted tabular">
        {b.activeLoans}/{b.loanLimit}
      </span>
      {b.overdueLoans > 0 ? (
        <Badge tone="danger" size="sm">
          {t('{{count}} overdue', { count: b.overdueLoans })}
        </Badge>
      ) : null}
    </div>
  )
}

/**
 * Issue / return desk: find a borrower, see their loans, issue by searching the catalogue.
 * Fully keyboard-driven: "/" focuses the active search box; ↑/↓/Enter pick; Esc clears or goes back.
 */
export function CirculationPage() {
  const { t } = useTranslation('library')
  useDocumentTitle('Circulation desk')
  const { school } = useAuth()
  const currency = school?.currency ?? 'USD'
  const [params, setParams] = useSearchParams()
  const selected = parseBorrower(params.get('borrower'))
  const [who, setWho] = useState('')
  const [what, setWhat] = useState('')
  const [dueOn, setDueOn] = useState<string | null>(null)
  const whoRef = useRef<HTMLInputElement>(null)
  const whatRef = useRef<HTMLInputElement>(null)
  const whoTerm = useDebouncedValue(who, 200)
  const whatTerm = useDebouncedValue(what, 200)

  const settings = useQuery(librarySettingsQuery())
  const borrowers = useQuery(borrowersQuery(whoTerm))
  const detail = useQuery({
    ...borrowerQuery(selected?.kind ?? 'student', selected?.id ?? 0),
    enabled: selected !== null,
  })
  const books = useQuery({
    ...booksQuery({ search: whatTerm, perPage: 8, sort: 'title' }),
    enabled: selected !== null && whatTerm.trim().length > 0,
  })
  const issue = useIssueLoan()

  const defaultDue = addDaysIso(todayIso(), settings.data?.loanDays ?? 14)
  const due = dueOn ?? defaultDue
  const borrower = detail.data?.borrower
  const openLoans = detail.data?.loans.filter((l) => l.state !== 'returned') ?? []
  const history = detail.data?.loans.filter((l) => l.state === 'returned') ?? []
  const atLimit = borrower ? borrower.activeLoans >= borrower.loanLimit : false

  // "/" jumps to the relevant search box from anywhere on the page.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (e.key !== '/' || target?.closest('input, textarea, select, [contenteditable="true"]')) return
      e.preventDefault()
      ;(selected ? whatRef : whoRef).current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selected])

  const choose = (b: Borrower) => {
    setParams({ borrower: `${b.kind}-${b.id}` }, { replace: true })
    setWho('')
    setWhat('')
    requestAnimationFrame(() => whatRef.current?.focus())
  }

  const clearBorrower = () => {
    setParams({}, { replace: true })
    setWhat('')
    setDueOn(null)
    requestAnimationFrame(() => whoRef.current?.focus())
  }

  const reasonFor = (book: Book): string | null => {
    if (!book.active) return t('Withdrawn')
    if (openLoans.some((l) => l.book.id === book.id)) return t('Already borrowed')
    if (book.availableCopies < 1) return t('All on loan')
    if (atLimit) return t('Limit reached')
    return null
  }

  const issueBook = async (book: Book) => {
    if (!selected) return
    try {
      const loan = await issue.mutateAsync({
        bookId: book.id,
        borrowerKind: selected.kind,
        borrowerId: selected.id,
        dueOn: dueOn,
      })
      toast.success(t('Book issued'), `${loan.book.title} → ${loan.borrower.name}`)
      setWhat('')
      whatRef.current?.focus()
    } catch (e) {
      toast.error(t('Could not issue the book'), errorMessage(e))
    }
  }

  return (
    <>
      <PageHeader
        title={t('Circulation desk')}
        description={t('Find a borrower, then issue, return or renew their books.')}
        breadcrumbs={[{ label: t('Library'), to: '/admin/library' }, { label: t('Circulation') }]}
        homeHref="/admin"
        meta={
          <span className="inline-flex items-center gap-1.5 text-xs text-muted">
            {t('Press')} <Kbd>/</Kbd> {t('to search')} ·{' '}
            {settings.data
              ? t('loan period {{count}} days', { count: settings.data.loanDays })
              : t('loan period … days')}
          </span>
        }
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader
            title={t('Borrower')}
            icon={<UserRoundSearch />}
            description={t('Student or staff, by name or code')}
          />
          <CardBody>
            {selected === null ? (
              <SearchCombobox
                label={t('Find a borrower')}
                placeholder={t('Type a name, student code or employee code…')}
                value={who}
                onValueChange={setWho}
                items={borrowers.data}
                loading={borrowers.isFetching}
                getKey={(b) => `${b.kind}-${b.id}`}
                renderItem={(b) => <BorrowerLine b={b} />}
                onSelect={choose}
                inputRef={whoRef}
                autoFocus
                emptyText={t('No active student or staff member matches.')}
              />
            ) : detail.isPending ? (
              <div className="flex items-center gap-3">
                <Skeleton className="size-14 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-3 w-1/3" />
                </div>
              </div>
            ) : detail.error ? (
              <ErrorState onRetry={() => void detail.refetch()} compact />
            ) : borrower ? (
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <Avatar name={borrower.name} src={borrower.photoUrl} size="lg" />
                  <div className="min-w-0 flex-1">
                    <p className="text-lg font-semibold text-ink">{borrower.name}</p>
                    <p className="text-sm text-muted">
                      {humanize(borrower.kind)} · <span className="font-mono text-xs">{borrower.code}</span>
                      {borrower.subtitle ? ` · ${borrower.subtitle}` : ''}
                    </p>
                  </div>
                  <IconButton
                    label={t('Change borrower (Esc in the book search)')}
                    icon={<X />}
                    onClick={clearBorrower}
                  />
                </div>
                <dl className="grid grid-cols-3 gap-3 text-center">
                  <div className="rounded-control bg-surface-muted p-2.5">
                    <dt className="text-2xs text-muted uppercase">{t('On loan')}</dt>
                    <dd className="mt-0.5 font-semibold text-ink tabular">
                      {borrower.activeLoans} / {borrower.loanLimit}
                    </dd>
                  </div>
                  <div className="rounded-control bg-surface-muted p-2.5">
                    <dt className="text-2xs text-muted uppercase">{t('Overdue')}</dt>
                    <dd
                      className={
                        borrower.overdueLoans
                          ? 'mt-0.5 font-semibold text-danger-700 tabular'
                          : 'mt-0.5 font-semibold text-ink tabular'
                      }
                    >
                      {borrower.overdueLoans}
                    </dd>
                  </div>
                  <div className="rounded-control bg-surface-muted p-2.5">
                    <dt className="text-2xs text-muted uppercase">{t('Unpaid fines')}</dt>
                    <dd className="mt-0.5 font-semibold text-ink tabular">
                      {formatMoney(borrower.finesOutstanding, currency)}
                    </dd>
                  </div>
                </dl>
                {!borrower.active ? (
                  <Alert tone="warning">
                    {t('This borrower is no longer active and cannot borrow books.')}
                  </Alert>
                ) : null}
                {isPositiveAmount(borrower.finesOutstanding) ? (
                  <Alert tone="warning">{t('Unpaid fines — collect them from the history below.')}</Alert>
                ) : null}
              </div>
            ) : null}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title={t('Issue a book')}
            icon={<BookPlus />}
            description={borrower ? t('To {{name}}', { name: borrower.name }) : t('Choose a borrower first')}
          />
          <CardBody className="space-y-4">
            {selected === null ? (
              <EmptyState
                icon={<ArrowLeftRight />}
                title={t('No borrower selected')}
                description={t('Search for a student or staff member on the left.')}
                compact
              />
            ) : (
              <>
                {atLimit ? (
                  <Alert tone="warning">
                    {t('{{name}} has reached the limit of {{count}} books.', {
                      name: borrower?.name,
                      count: borrower?.loanLimit ?? 0,
                    })}
                  </Alert>
                ) : null}
                <SearchCombobox
                  label={t('Find a book')}
                  placeholder={t('Title, author or ISBN…')}
                  value={what}
                  onValueChange={setWhat}
                  items={books.data?.data}
                  loading={books.isFetching || issue.isPending}
                  getKey={(b) => b.id}
                  disabledReason={reasonFor}
                  renderItem={(b) => (
                    <div className="flex items-center gap-3">
                      <BookCover title={b.title} src={b.coverUrl} size="xs" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-ink">{b.title}</p>
                        <p className="truncate text-xs text-muted">{b.authors}</p>
                      </div>
                      <AvailabilityBadge book={b} />
                    </div>
                  )}
                  onSelect={(b) => void issueBook(b)}
                  onEscape={clearBorrower}
                  inputRef={whatRef}
                  emptyText={t('No book matches.')}
                  hint={
                    <>
                      <Kbd>{t('Enter')}</Kbd> {t('issues the highlighted book')} · <Kbd>{t('Esc')}</Kbd>{' '}
                      {t('on an empty box changes borrower')}
                    </>
                  }
                />
                <FormField
                  label={t('Due date')}
                  hint={
                    dueOn ? undefined : t('Default: {{count}} days', { count: settings.data?.loanDays ?? 14 })
                  }
                >
                  <Input
                    type="date"
                    value={due}
                    min={addDaysIso(todayIso(), 1)}
                    onChange={(e) => setDueOn(e.target.value || null)}
                    className="sm:max-w-48"
                  />
                </FormField>
              </>
            )}
          </CardBody>
        </Card>
      </div>

      {selected !== null && detail.data ? (
        <div className="mt-6 space-y-6">
          <Card>
            <CardHeader
              title={t('Current loans')}
              description={
                openLoans.length ? t('{{count}} books out', { count: openLoans.length }) : undefined
              }
            />
            <LoanTable
              caption={t("Borrower's current loans")}
              loans={openLoans}
              currency={currency}
              showBorrower={false}
              actions
              bookHref={(l) => `/admin/library/books/${l.book.id}`}
              empty={
                <EmptyState
                  title={t('Nothing on loan')}
                  description={t('Issued books will appear here.')}
                  compact
                />
              }
            />
          </Card>
          {history.length ? (
            <Card>
              <CardHeader title={t('Recent history')} />
              <LoanTable
                caption={t("Borrower's loan history")}
                loans={history}
                currency={currency}
                showBorrower={false}
                actions
                bookHref={(l) => `/admin/library/books/${l.book.id}`}
              />
            </Card>
          ) : null}
        </div>
      ) : null}
    </>
  )
}
